<template>
  <div class="mt-4 px-4 container-fluid">
    <div class="breadcrumb text-md px-0 mb-4">
      <p>
        <span class="breadcrumb-item" @click="$router.push('/dashboard')">Inicio</span>
        &rsaquo; <span class="breadcrumb-item" @click="$router.push('/informes')">Informes</span>
        &rsaquo; Informe de Suscripciones
      </p>
    </div>
  </div>

  <div class="px-4 container-fluid inf-wrap">

    <!-- Selector de productos -->
    <div class="card ficha-listado mb-4">
      <div class="card-body px-3 px-md-4 pt-3 pb-3">
        <div class="row g-3 align-items-end">
          <div class="col-12 col-md-6 col-lg-5">
            <label class="form-label form-label-sm">
              Productos
              <span v-if="productosSeleccionados.length" class="badge-sel ms-1">
                {{ productosSeleccionados.length }} seleccionado{{ productosSeleccionados.length > 1 ? 's' : '' }}
              </span>
            </label>
            <div v-if="cargandoProductos" class="text-muted" style="font-size:13px">Cargando productos…</div>
            <div v-else-if="!productosDisponibles.length" class="text-muted" style="font-size:13px">Sin productos con suscripciones</div>
            <div v-else class="multi-check-list">
              <label
                v-for="p in productosDisponibles"
                :key="p.id"
                class="multi-check-item"
                :class="{ 'is-checked': productosSeleccionados.includes(p.id) }"
              >
                <input
                  type="checkbox"
                  :value="p.id"
                  v-model="productosSeleccionados"
                  class="multi-check-input"
                />
                <span>{{ p.nombre }}</span>
                <span class="multi-check-moneda">{{ p.moneda }}</span>
              </label>
            </div>
          </div>
          <div class="col-auto d-flex flex-column gap-2">
            <button
              class="inf-btn-reload"
              @click="cargar"
              :disabled="cargando || !productosSeleccionados.length"
            >
              <i class="fa fa-sync-alt" :class="{ 'fa-spin': cargando }"></i>
              {{ cargando ? 'Cargando…' : 'Generar informe' }}
            </button>
            <button v-if="data.length" class="inf-btn-pdf" :disabled="generandoPdf" @click="generarPdf">
              <i class="fa fa-file-pdf me-1"></i>
              {{ generandoPdf ? 'Generando…' : 'Generar PDF' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Aviso sin Stripe -->
    <div v-if="!cargando && data.length && !data[0].stripe_ok" class="inf-aviso-stripe mb-3">
      <i class="fa fa-exclamation-triangle"></i>
      Los datos de renovaciones requieren acceso a Stripe y no están disponibles en este entorno.
      Los conteos marcados con <span class="inf-badge-aprox">aprox.</span> son estimaciones locales.
    </div>

    <!-- Error -->
    <div v-if="error" class="inf-error mb-3">
      <i class="fa fa-times-circle"></i> {{ error }}
    </div>

    <!-- Vacío -->
    <div v-if="!cargando && !data.length && !error && productosSeleccionados.length" class="text-muted text-center py-5">
      Sin suscripciones para este producto.
    </div>

    <!-- Hint inicial -->
    <div v-if="!productosSeleccionados.length && !cargando" class="text-muted text-center py-5" style="font-size:13px">
      <i class="fa fa-arrow-up me-1"></i> Selecciona un producto para ver el funnel
    </div>

    <!-- Loading skeleton -->
    <div v-if="cargando" class="inf-skeleton">
      <div class="inf-skeleton-card" v-for="n in 2" :key="n">
        <div class="inf-skeleton-line inf-skeleton-title"></div>
        <div class="inf-skeleton-line" v-for="r in 5" :key="r"></div>
      </div>
    </div>

    <!-- Productos -->
    <div v-if="!cargando && data.length" class="inf-productos">
      <div v-for="prod in data" :key="prod.producto_id" class="inf-producto-card">

        <!-- Cabecera producto -->
        <div class="inf-prod-header">
          <span class="inf-prod-nombre">{{ prod.nombre }}</span>
          <span class="inf-prod-moneda">{{ prod.moneda }}</span>
          <span v-if="!prod.stripe_ok" class="inf-badge-aprox">sin Stripe</span>
        </div>

        <!-- Totales -->
        <div class="inf-totales-row">
          <div class="inf-total-chip">
            <span class="inf-total-num">{{ prod.gratuitos.total + prod.pagadores.total }}</span>
            <span class="inf-total-label">suscripciones totales</span>
          </div>
          <div class="inf-total-chip">
            <span class="inf-total-num">{{ prod.gratuitos.total }}</span>
            <span class="inf-total-label">con trial</span>
          </div>
          <div class="inf-total-chip">
            <span class="inf-total-num">{{ prod.pagadores.total }}</span>
            <span class="inf-total-label">pago directo</span>
          </div>
        </div>

        <!-- Dos tablas -->
        <div class="inf-tablas">

          <!-- Listado 1: Gratuitos -->
          <div class="inf-tabla-bloque">
            <div class="inf-tabla-titulo">
              <i class="fa fa-gift"></i> Listado 1 — Gratuitos (con trial)
            </div>
            <table class="inf-tabla">
              <tbody>
                <tr>
                  <td class="inf-td-label">Registros que usaron días gratis</td>
                  <td class="inf-td-num inf-num-total">{{ prod.gratuitos.total }}</td>
                </tr>
                <tr>
                  <td class="inf-td-label">Se dieron de baja antes del primer cobro</td>
                  <td class="inf-td-num inf-num-baja">{{ prod.gratuitos.baja_antes_cobro }}</td>
                  <td class="inf-td-pct">{{ pct(prod.gratuitos.baja_antes_cobro, prod.gratuitos.total) }}</td>
                </tr>
                <tr>
                  <td class="inf-td-label">No se dieron de baja pero falló el pago</td>
                  <td class="inf-td-num inf-num-fallo">{{ prod.gratuitos.fallo_pago }}</td>
                  <td class="inf-td-pct">{{ pct(prod.gratuitos.fallo_pago, prod.gratuitos.total) }}</td>
                </tr>
                <tr>
                  <td class="inf-td-label">Se les cobró el primer pago</td>
                  <td class="inf-td-num inf-num-ok">{{ prod.gratuitos.pagaron_1 }}</td>
                  <td class="inf-td-pct">{{ pct(prod.gratuitos.pagaron_1, prod.gratuitos.total) }}</td>
                </tr>
                <tr>
                  <td class="inf-td-label">Se les cobró el segundo pago</td>
                  <td class="inf-td-num inf-num-ok">{{ prod.gratuitos.pagaron_2 }}</td>
                  <td class="inf-td-pct">{{ pct(prod.gratuitos.pagaron_2, prod.gratuitos.total) }}</td>
                </tr>
              </tbody>
            </table>
            <div class="inf-funnel">
              <div
                v-for="(step, i) in funnelGratuitos(prod.gratuitos)"
                :key="i"
                class="inf-funnel-step"
                :style="{ width: stepWidth(step.val, prod.gratuitos.total) }"
                :class="step.cls"
              >
                <span class="inf-funnel-label">{{ step.label }}</span>
                <span class="inf-funnel-val">{{ step.val }}</span>
              </div>
            </div>
          </div>

          <!-- Listado 2: Pagadores -->
          <div class="inf-tabla-bloque">
            <div class="inf-tabla-titulo">
              <i class="fa fa-credit-card"></i> Listado 2 — Pagadores de inicio
            </div>
            <table class="inf-tabla">
              <tbody>
                <tr>
                  <td class="inf-td-label">Se registraron pagando directamente</td>
                  <td class="inf-td-num inf-num-total">{{ prod.pagadores.total }}</td>
                </tr>
                <tr>
                  <td class="inf-td-label">Se dieron de baja antes de la primera renovación</td>
                  <td class="inf-td-num inf-num-baja">{{ prod.pagadores.baja_antes_renovar }}</td>
                  <td class="inf-td-pct">{{ pct(prod.pagadores.baja_antes_renovar, prod.pagadores.total) }}</td>
                </tr>
                <tr>
                  <td class="inf-td-label">No se dieron de baja pero falló la primera renovación</td>
                  <td class="inf-td-num inf-num-fallo">{{ prod.pagadores.fallo_renovacion_1 }}</td>
                  <td class="inf-td-pct">{{ pct(prod.pagadores.fallo_renovacion_1, prod.pagadores.total) }}</td>
                </tr>
                <tr>
                  <td class="inf-td-label">Se les cobró la primera renovación</td>
                  <td class="inf-td-num inf-num-ok">{{ prod.pagadores.renovacion_1 }}</td>
                  <td class="inf-td-pct">{{ pct(prod.pagadores.renovacion_1, prod.pagadores.total) }}</td>
                </tr>
                <tr>
                  <td class="inf-td-label">Se les cobró la segunda renovación</td>
                  <td class="inf-td-num inf-num-ok">{{ prod.pagadores.renovacion_2 }}</td>
                  <td class="inf-td-pct">{{ pct(prod.pagadores.renovacion_2, prod.pagadores.total) }}</td>
                </tr>
              </tbody>
            </table>
            <div class="inf-funnel">
              <div
                v-for="(step, i) in funnelPagadores(prod.pagadores)"
                :key="i"
                class="inf-funnel-step"
                :style="{ width: stepWidth(step.val, prod.pagadores.total) }"
                :class="step.cls"
              >
                <span class="inf-funnel-label">{{ step.label }}</span>
                <span class="inf-funnel-val">{{ step.val }}</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Nota metodológica -->
    <div v-if="!cargando && data.length" class="inf-nota mt-4">
      <i class="fa fa-info-circle"></i>
      <strong>Metodología:</strong>
      Trial = suscripción con campo <code>gracia_hasta</code> en BD.
      Conteos de renovaciones extraídos de Stripe Invoice API (facturas con amount_due &gt; 0 en estado <code>paid</code>).
      La BD local se usa para el estado actual (activa / cancelada / vencida / gracia).
      Generado el {{ fechaGeneracion }}.
    </div>

  </div>
</template>

<script>
import api from "@/services/apiClient.js";

export default {
  name: "InformePedidos",
  data() {
    return {
      tipsterId:             null,
      tipsterNombre:         "",
      productosSeleccionados: [],
      productosDisponibles:  [],
      cargandoProductos:     false,
      cargando:              false,
      error:                 null,
      data:                  [],
      fechaGeneracion:       "",
      generandoPdf:          false,
    };
  },
  async mounted() {
    const q = this.$route.query;
    if (q.tipster_id) this.tipsterId = parseInt(q.tipster_id);
    await this.cargarProductos();
  },
  methods: {
    async cargarProductos() {
      this.cargandoProductos = true;
      try {
        const params = this.tipsterId ? { tipster_id: this.tipsterId } : {};
        const { data } = await api.get("/api/v2/informes/productos", { params });
        this.productosDisponibles  = data.productos || [];
        this.tipsterNombre         = data.tipster_nombre || "";
        // Si solo hay un producto, seleccionarlo automáticamente
        if (this.productosDisponibles.length === 1) {
          this.productosSeleccionados = [this.productosDisponibles[0].id];
          this.cargar();
        }
      } catch (e) {
        this.error = "Error al cargar productos.";
      } finally {
        this.cargandoProductos = false;
      }
    },

    async cargar() {
      if (!this.productosSeleccionados.length) return;
      this.cargando = true;
      this.error = null;
      try {
        const llamadas = this.productosSeleccionados.map(pid => {
          const params = { producto_id: pid };
          if (this.tipsterId) params.tipster_id = this.tipsterId;
          return api.get("/api/v2/informes/suscripciones", { params });
        });
        const respuestas = await Promise.all(llamadas);
        this.data = respuestas.flatMap(r => r.data.data || []);
        this.fechaGeneracion = new Date().toLocaleString("es-ES");
      } catch (e) {
        this.error = e?.response?.data?.message || "Error al cargar el informe.";
      } finally {
        this.cargando = false;
      }
    },

    pct(val, total) {
      if (!total) return "—";
      return Math.round((val / total) * 100) + "%";
    },

    stepWidth(val, total) {
      if (!total) return "30%";
      const p = Math.max(20, Math.round((val / total) * 100));
      return p + "%";
    },

    funnelGratuitos(g) {
      return [
        { label: "Total",    val: g.total,            cls: "inf-funnel-total" },
        { label: "Baja",     val: g.baja_antes_cobro, cls: "inf-funnel-baja" },
        { label: "Fallo",    val: g.fallo_pago,        cls: "inf-funnel-fallo" },
        { label: "1er pago", val: g.pagaron_1,         cls: "inf-funnel-ok" },
        { label: "2º pago",  val: g.pagaron_2,         cls: "inf-funnel-ok2" },
      ];
    },

    funnelPagadores(p) {
      return [
        { label: "Total",    val: p.total,              cls: "inf-funnel-total" },
        { label: "Baja",     val: p.baja_antes_renovar, cls: "inf-funnel-baja" },
        { label: "Fallo",    val: p.fallo_renovacion_1, cls: "inf-funnel-fallo" },
        { label: "Renov. 1", val: p.renovacion_1,       cls: "inf-funnel-ok" },
        { label: "Renov. 2", val: p.renovacion_2,       cls: "inf-funnel-ok2" },
      ];
    },

    generarPdf() {
      this.generandoPdf = true;
      try {
        const html = this.buildPdfHtml();
        const win  = window.open("", "_blank");
        if (!win) { alert("Permite ventanas emergentes para generar el PDF."); return; }
        win.document.write(html);
        win.document.close();
        win.focus();
        setTimeout(() => { win.print(); }, 700);
      } finally {
        this.generandoPdf = false;
      }
    },

    buildPdfHtml() {
      const logoUrl  = window.location.origin + "/img/quanty.png";
      const fechaGen = new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
      const pct = (val, total) => total ? Math.round((val / total) * 100) + "%" : "—";

      const bloques = this.data.map(prod => {
        const totalSubs = prod.gratuitos.total + prod.pagadores.total;
        return `
          <div class="prod-bloque">
            <div class="prod-nombre">${prod.nombre} <span class="prod-moneda">${prod.moneda}</span></div>
            <div class="chips">
              <span class="chip chip-total">${totalSubs} suscripciones totales</span>
              <span class="chip chip-trial">${prod.gratuitos.total} con trial</span>
              <span class="chip chip-pago">${prod.pagadores.total} pago directo</span>
            </div>
            <div class="tablas">
              <div class="tabla-bloque">
                <div class="tabla-titulo">Listado 1 — Gratuitos (con trial)</div>
                <table>
                  <tr><td>Registros con días gratis</td>
                      <td class="num total">${prod.gratuitos.total}</td><td class="pct"></td></tr>
                  <tr><td>Baja antes del primer cobro</td>
                      <td class="num baja">${prod.gratuitos.baja_antes_cobro}</td>
                      <td class="pct">${pct(prod.gratuitos.baja_antes_cobro, prod.gratuitos.total)}</td></tr>
                  <tr><td>Sin baja pero falló el pago</td>
                      <td class="num fallo">${prod.gratuitos.fallo_pago}</td>
                      <td class="pct">${pct(prod.gratuitos.fallo_pago, prod.gratuitos.total)}</td></tr>
                  <tr><td>Se cobró el primer pago</td>
                      <td class="num ok">${prod.gratuitos.pagaron_1}</td>
                      <td class="pct">${pct(prod.gratuitos.pagaron_1, prod.gratuitos.total)}</td></tr>
                  <tr><td>Se cobró el segundo pago</td>
                      <td class="num ok">${prod.gratuitos.pagaron_2}</td>
                      <td class="pct">${pct(prod.gratuitos.pagaron_2, prod.gratuitos.total)}</td></tr>
                </table>
              </div>
              <div class="tabla-bloque">
                <div class="tabla-titulo">Listado 2 — Pagadores de inicio</div>
                <table>
                  <tr><td>Pagaron directamente al registrarse</td>
                      <td class="num total">${prod.pagadores.total}</td><td class="pct"></td></tr>
                  <tr><td>Baja antes de la primera renovación</td>
                      <td class="num baja">${prod.pagadores.baja_antes_renovar}</td>
                      <td class="pct">${pct(prod.pagadores.baja_antes_renovar, prod.pagadores.total)}</td></tr>
                  <tr><td>Sin baja pero falló la renovación</td>
                      <td class="num fallo">${prod.pagadores.fallo_renovacion_1}</td>
                      <td class="pct">${pct(prod.pagadores.fallo_renovacion_1, prod.pagadores.total)}</td></tr>
                  <tr><td>Se cobró la primera renovación</td>
                      <td class="num ok">${prod.pagadores.renovacion_1}</td>
                      <td class="pct">${pct(prod.pagadores.renovacion_1, prod.pagadores.total)}</td></tr>
                  <tr><td>Se cobró la segunda renovación</td>
                      <td class="num ok">${prod.pagadores.renovacion_2}</td>
                      <td class="pct">${pct(prod.pagadores.renovacion_2, prod.pagadores.total)}</td></tr>
                </table>
              </div>
            </div>
          </div>`;
      }).join("");

      return `<!DOCTYPE html><html lang="es"><head>
        <meta charset="UTF-8">
        <title>Informe de Suscripciones</title>
        <style>
          *{box-sizing:border-box;margin:0;padding:0}
          body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:24px}
          .header{display:flex;justify-content:space-between;align-items:flex-start;
                  margin-bottom:18px;border-bottom:2px solid #c8a94a;padding-bottom:12px}
          .logo{height:36px}
          .meta{text-align:right;color:#555;font-size:10px}
          h2{font-size:16px;margin-bottom:2px}
          .tipster-nombre{font-size:12px;color:#c8a94a;font-weight:600;margin-bottom:2px}
          .subtitulo{font-size:11px;color:#666;margin-bottom:18px}
          .prod-bloque{margin-bottom:28px;page-break-inside:avoid}
          .prod-nombre{font-size:13px;font-weight:700;color:#c8a94a;margin-bottom:8px}
          .prod-moneda{font-size:10px;background:#f0e8d0;color:#8a6a10;padding:1px 6px;
                       border-radius:3px;margin-left:6px;font-weight:400}
          .chips{display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap}
          .chip{font-size:10px;padding:2px 10px;border-radius:10px;font-weight:600}
          .chip-total{background:#f0e8d0;color:#8a6a10}
          .chip-trial{background:#d0e8f0;color:#106a8a}
          .chip-pago{background:#d0f0d8;color:#108a3a}
          .tablas{display:grid;grid-template-columns:1fr 1fr;gap:16px}
          .tabla-bloque{border:1px solid #ddd;border-radius:6px;padding:12px}
          .tabla-titulo{font-size:10px;font-weight:700;text-transform:uppercase;
                        color:#c8a94a;margin-bottom:8px;letter-spacing:.04em}
          table{width:100%;border-collapse:collapse;font-size:10px}
          tr{border-bottom:1px solid #f0f0f0}
          tr:last-child{border-bottom:none}
          td{padding:4px 3px;vertical-align:middle}
          .num{text-align:right;font-weight:700;font-size:12px;padding-left:8px;white-space:nowrap}
          .pct{text-align:right;color:#888;font-size:9px;white-space:nowrap;padding-left:4px}
          .total{color:#c8a94a}.baja{color:#d07040}.fallo{color:#d04040}.ok{color:#40a060}
          .nota{font-size:9px;color:#888;border-top:1px solid #eee;margin-top:16px;padding-top:8px;line-height:1.5}
          @media print{body{padding:10px}.prod-bloque{page-break-inside:avoid}@page{margin:15mm}}
        </style>
      </head><body>
        <div class="header">
          <img src="${logoUrl}" class="logo" onerror="this.style.display='none'">
          <div class="meta"><div>Generado el ${fechaGen}</div></div>
        </div>
        <h2>Informe de Suscripciones</h2>
        ${this.tipsterNombre ? `<p class="tipster-nombre">${this.tipsterNombre}</p>` : ''}
        <p class="subtitulo">Generado el ${this.fechaGeneracion}</p>
        ${bloques}
      </body></html>`;
    },
  },
};
</script>

<style scoped>
.inf-wrap { max-width: 1200px; }

.inf-btn-reload {
  background: #1a1a2e; color: #fff; border: none; border-radius: 6px;
  padding: 6px 14px; font-size: 0.82rem; cursor: pointer;
  display: flex; align-items: center; gap: 6px;
}
.inf-btn-reload:disabled { opacity: 0.6; cursor: not-allowed; }

.inf-btn-pdf {
  background: #6b2020; color: #fff; border: none; border-radius: 6px;
  padding: 6px 14px; font-size: 0.82rem; cursor: pointer;
  display: flex; align-items: center; gap: 6px;
}
.inf-btn-pdf:disabled { opacity: 0.6; cursor: not-allowed; }

.badge-sel {
  background: #c8a94a22; color: #c8a94a; border: 1px solid #c8a94a55;
  border-radius: 10px; font-size: 0.72rem; padding: 1px 7px; font-weight: 600;
}
.multi-check-list {
  display: flex; flex-direction: column; gap: 2px;
  max-height: 220px; overflow-y: auto;
  border: 1px solid var(--q-border, #2a2a4a); border-radius: 8px; padding: 6px 4px;
}
.multi-check-item {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 10px; border-radius: 6px; cursor: pointer;
  font-size: 0.83rem; color: var(--q-text, #ccc);
  transition: background .15s;
}
.multi-check-item:hover { background: var(--q-hover, #1a1a3a); }
.multi-check-item.is-checked { background: #c8a94a18; color: #e8c56d; }
.multi-check-input { accent-color: #c8a94a; width: 14px; height: 14px; flex-shrink: 0; }
.multi-check-moneda {
  margin-left: auto; font-size: 0.72rem;
  background: #2a2a4a; color: #888; padding: 1px 6px; border-radius: 3px;
}

.inf-aviso-stripe { background: #fff3cd; color: #856404; border-left: 3px solid #ffc107; padding: 8px 14px; border-radius: 6px; font-size: 0.82rem; }
.inf-error { background: #f8d7da; color: #721c24; border-left: 3px solid #dc3545; padding: 8px 14px; border-radius: 6px; font-size: 0.82rem; }

.inf-skeleton { display: flex; gap: 20px; flex-wrap: wrap; }
.inf-skeleton-card { flex: 1; min-width: 300px; background: #1a1a2e; border-radius: 10px; padding: 20px; }
.inf-skeleton-line { height: 14px; background: linear-gradient(90deg, #2a2a4a 25%, #3a3a5a 50%, #2a2a4a 75%); background-size: 200% 100%; animation: shimmer 1.4s infinite; border-radius: 4px; margin-bottom: 10px; }
.inf-skeleton-title { height: 18px; width: 60%; margin-bottom: 16px; }
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

.inf-productos { display: flex; flex-direction: column; gap: 24px; }
.inf-producto-card { background: #12122a; border: 1px solid #2a2a4a; border-radius: 12px; padding: 20px 24px; }
.inf-prod-header { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.inf-prod-nombre { font-size: 1rem; font-weight: 700; color: #e8c56d; letter-spacing: 0.5px; }
.inf-prod-moneda { font-size: 0.75rem; background: #2a2a4a; color: #aaa; padding: 2px 8px; border-radius: 4px; }
.inf-badge-aprox { font-size: 0.72rem; background: #ffc10733; color: #ffc107; padding: 2px 8px; border-radius: 4px; border: 1px solid #ffc10755; }

.inf-totales-row { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
.inf-total-chip { background: #1a1a3a; border: 1px solid #3a3a5a; border-radius: 8px; padding: 8px 16px; display: flex; flex-direction: column; align-items: center; min-width: 90px; }
.inf-total-num { font-size: 1.4rem; font-weight: 700; color: #e8c56d; }
.inf-total-label { font-size: 0.72rem; color: #888; text-align: center; }

.inf-tablas { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
@media (max-width: 800px) { .inf-tablas { grid-template-columns: 1fr; } }
.inf-tabla-bloque { background: #0f0f22; border: 1px solid #2a2a4a; border-radius: 8px; padding: 16px; }
.inf-tabla-titulo { font-size: 0.82rem; font-weight: 600; color: #c5a030; margin-bottom: 12px; display: flex; align-items: center; gap: 6px; }
.inf-tabla { width: 100%; border-collapse: collapse; font-size: 0.82rem; margin-bottom: 16px; }
.inf-tabla tr { border-bottom: 1px solid #1a1a3a; }
.inf-tabla tr:last-child { border-bottom: none; }
.inf-td-label { color: #ccc; padding: 8px 4px; line-height: 1.3; }
.inf-td-num { text-align: right; font-size: 1rem; font-weight: 700; padding: 8px 8px 8px 12px; white-space: nowrap; min-width: 40px; }
.inf-td-pct { text-align: right; color: #666; font-size: 0.75rem; padding: 8px 4px; white-space: nowrap; min-width: 40px; }
.inf-num-total { color: #e8c56d; }
.inf-num-baja  { color: #e88c6d; }
.inf-num-fallo { color: #e85454; }
.inf-num-ok    { color: #6de8a0; }

.inf-funnel { display: flex; flex-direction: column; gap: 4px; margin-top: 8px; }
.inf-funnel-step { display: flex; align-items: center; justify-content: space-between; padding: 4px 10px; border-radius: 4px; font-size: 0.75rem; min-width: 80px; transition: width 0.4s ease; }
.inf-funnel-label { color: rgba(255,255,255,0.7); font-size: 0.72rem; }
.inf-funnel-val   { font-weight: 700; }
.inf-funnel-total { background: #2a2a5a; color: #e8c56d; }
.inf-funnel-baja  { background: #3a2a1a; color: #e88c6d; }
.inf-funnel-fallo { background: #3a1a1a; color: #e85454; }
.inf-funnel-ok    { background: #1a3a2a; color: #6de8a0; }
.inf-funnel-ok2   { background: #1a2a3a; color: #6db8e8; }

.inf-nota { font-size: 0.78rem; color: #666; border-top: 1px solid #2a2a4a; padding-top: 14px; line-height: 1.6; }
.inf-nota code { background: #2a2a3a; color: #e8c56d; padding: 1px 5px; border-radius: 3px; font-size: 0.75rem; }
</style>
